import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError } from '@/api/client';
import { Tag, Dot } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Empty } from '@/components/States';
import { dateTime, money, num, qty } from '@/lib/format';
import { legacyHref } from '@/shell/nav';
import { useSession } from '@/stores/session';
import { DetailError, DetailLoading, DetailShell, KV, Kpis, Panel, detailStyles as d } from './kit/Detail';
import { imageSrc, Thumb } from './kit/Thumb';
import { useResource } from './kit/hooks';
import { availClass, bundleState, quantityMode } from './status';
import { availableIn, bundleFits, isGroup, needFor, needFormula, ruleText, setCapacity } from './rules';
import type { Bundle } from './types';
import s from './BundleDetail.module.css';

/** 试算默认套数：有按组计的组件就取「每组套数 × 2」，正好演示向上取整；否则 10 套 */
function defaultSets(b: Bundle) {
  const g = Math.max(0, ...b.items.filter(isGroup).map((i) => num(i.setsPerGroup)));
  return g > 0 ? g * 2 : 10;
}

export function BundleDetail({ id, embedded }: { id: string; embedded?: boolean }) {
  const can = useSession((state) => state.can);
  const path = `/api/bundles/${id}`;
  const { data: b, loading, error, reload } = useResource<Bundle>(path);
  const [wh, setWh] = useState<string>();
  const [sets, setSets] = useState<number>();

  if (error instanceof ApiError && error.status === 404) {
    return <Empty icon="info" title="套装不存在" action={<Link className={s.back} to="/bundles">回套装列表</Link>}>可能已被删除，或者链接有误。</Empty>;
  }
  if (error) return <DetailError title="套装加载失败" error={error} path={path} onRetry={reload} />;
  if (loading || !b) return <DetailLoading />;

  const fits = bundleFits(b);
  const fit = fits.find((f) => f.warehouseId === wh) || fits[0];
  const trial = sets ?? defaultSets(b);
  const groups = b.items.filter(isGroup);
  const st = bundleState(b.isActive);
  const g0 = groups[0];
  const g0n = g0 ? num(g0.setsPerGroup) : 0;

  const main = (
    <>
      <Kpis
        className={embedded ? s.embeddedKpis : undefined}
        items={[
          { label: '现在可配齐', value: fit ? `${qty(fit.sets)}` : '—', unit: fit ? '套' : undefined, tone: fit && fit.sets <= 0 ? 'neg' : 'hl' },
          { label: '卡在', value: fit?.limit ? fit.limit.product.name : '—' },
          { label: '组件', value: `${b.items.length}`, unit: groups.length ? `个 · ${groups.length} 个按组` : '个' },
          ...(b.totalCost != null ? [{ label: '套装总成本', value: money(b.totalCost) }] : []),
        ]}
      />

      {fits.length > 1 && (
        <div className={s.whs} role="group" aria-label="按仓库看">
          <span>按仓库</span>
          {fits.map((f) => (
            <button key={f.warehouseId} type="button" className={f.warehouseId === fit?.warehouseId ? s.on : ''} onClick={() => setWh(f.warehouseId)}>
              {f.warehouseName || '仓库'} <b className="tnum">{qty(f.sets)} 套</b>
            </button>
          ))}
        </div>
      )}

      <Panel
        title="组件与用量规则"
        note={fit?.warehouseName ? `可用按 ${fit.warehouseName} 算` : '按当前可用库存算'}
        extra={
          <label className={s.trial}>
            试算
            <button type="button" aria-label="少 1 套" onClick={() => setSets(Math.max(1, trial - 1))}><Icon name="minus" size={14} /></button>
            <input type="number" inputMode="numeric" min={1} value={trial} onChange={(e) => setSets(Math.max(1, Math.floor(Number(e.target.value) || 1)))} aria-label="试算套数" />
            <button type="button" aria-label="多 1 套" onClick={() => setSets(trial + 1)}><Icon name="plus" size={14} /></button>
            套
          </label>
        }
        flush
      >
        <table className={`${d.tbl} ${embedded ? s.embeddedTable : ''}`}>
          <thead>
            <tr>
              <th>组件</th>
              {embedded && <th>编号</th>}
              <th className={s.hideNarrow}>用量规则</th>
              <th className={d.r}>下 {qty(trial)} 套需要</th>
              <th className={d.r}>{embedded ? '可用库存' : '可用'}</th>
              {!embedded && <th className={`${d.r} ${s.hideNarrow}`}>能撑</th>}
              <th className={s.hideNarrow}>状态</th>
            </tr>
          </thead>
          <tbody>
            {b.items.map((i) => {
              const avail = fit ? availableIn(i, fit.warehouseId) : 0;
              const need = needFor(i, trial);
              const m = quantityMode[isGroup(i) ? 'PER_GROUP' : 'PER_SET'];
              return (
                <tr key={i.id} className={isGroup(i) ? s.grp : undefined}>
                  <td className={s.name}>{i.product.name}{!embedded && <small className="tnum">{i.product.code}<span className={s.ruleInline}> · {ruleText(i)}</span></small>}</td>
                  {embedded && <td className={`${s.code} tnum`}>{i.product.code}</td>}
                  <td className={s.hideNarrow}><span className={s.rule}><Tag tone={m.tone} sm>{m.label}</Tag>{ruleText(i)}</span></td>
                  <td className={d.r}><b>{qty(need)}</b>{!embedded && <small className={s.formula}>{needFormula(i, trial)}</small>}</td>
                  <td className={`${d.r} ${availClass(avail)}`}>{qty(avail)}<small className={`${s.stInline} ${avail < need ? 'neg' : ''}`}>{avail >= need ? '充足' : `不足 ${qty(need - avail)}`}</small></td>
                  {!embedded && <td className={`${d.r} ${s.hideNarrow}`}>{qty(setCapacity(i, avail))} 套</td>}
                  <td className={s.hideNarrow}>{avail >= need ? <Dot tone="green">充足</Dot> : <Dot tone="red">不足 {qty(need - avail)}</Dot>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!embedded && g0 && g0n > 0 && (
          <div className={s.rulebox}>
            <b>按组用量向上取整</b>
            <p>公式 ceil(套数 ÷ 每组套数) × 每组用量。以「{g0.product.name}」为例：下 {qty(g0n * 2)} 套需要 {qty(needFor(g0, g0n * 2))} 个；下 {qty(g0n * 2 + 1)} 套需要 {qty(needFor(g0, g0n * 2 + 1))} 个 —— 多出的 1 套也要占一组。</p>
          </div>
        )}
        {!embedded && <p className={s.note}>{fit ? `按当前可用库存，这个套装最多能配齐 ${qty(fit.sets)} 套${fit.limit ? `（受${fit.limit.product.name}限制）` : ''}。` : '组件都还没有库存记录。'}只是参考，下单不拦负库存。</p>}
      </Panel>

      {embedded && b.description && <Panel title="说明"><p className={s.description}>{b.description}</p></Panel>}

      {!!b.images?.length && (
        <Panel title="套装图片">
          <div className={s.imgs}>
            {b.images.map((im, k) => (
              <a key={im.id || k} href={imageSrc(im.url)} target="_blank" rel="noreferrer"><Thumb src={imageSrc(im.thumbnailUrl || im.url)} alt={im.alt || b.name} square /></a>
            ))}
          </div>
        </Panel>
      )}
    </>
  );

  const info = (
    <Panel title="基本信息">
      <KV
        rows={[
          ['套装编码', <span className="tnum">{b.code}</span>],
          ['状态', b.isActive ? '启用' : '停用'],
          ['套装总成本', b.totalCost != null ? <span className="tnum">{money(b.totalCost)}</span> : null],
          ['尺寸数据', b.dimensionText ? <span className={s.pre}>{b.dimensionText}</span> : null],
          ['说明', b.description ? <span className={s.pre}>{b.description}</span> : null],
          ['更新', <span className="tnum">{dateTime(b.updatedAt)}</span>],
        ]}
      />
    </Panel>
  );

  return (
    <DetailShell
      embedded={embedded}
      backTo="/bundles"
      title={b.name}
      badges={st && <Tag tone={st.tone}>{st.label}</Tag>}
      openHref={`/bundles/${b.id}`}
      actions={can('products.bundle.write') ? <>
        {embedded && <Link className={s.copy} to={`/bundles/${b.id}/copy`}>复制套装</Link>}
        <Link className={s.back} to={`/bundles/${b.id}/edit`}>编辑套装</Link>
      </> : undefined}
      legacy={legacyHref(`/bundles/${b.id}`)}
      sub={<>{b.code}{b.dimensionText ? ` · 尺寸 ${b.dimensionText}` : ''} · 套装本身不记库存，下单时按用量规则展开成产品行</>}
      main={main}
      aside={embedded ? undefined : info}
    />
  );
}
